import type { EquipmentSlot, Item } from '../equipment';
import { equipmentAffixPoolForFamily, type EquipmentTemplate } from './procedural';

export function unifiedEquipmentSlot(item: Partial<Item> | null | undefined): EquipmentSlot | null {
  if (!item) return null;
  if (item.unifiedSlot) return item.unifiedSlot;
  switch (item.slot) {
    case 'head': case 'hair': case 'hairFlower': case 'glasses': return 'hat';
    case 'necklace': case 'accessory': return 'accessory';
    case 'chest': case 'dress': return 'armor_top';
    case 'legs': case 'pants': return 'armor_bottom';
    case 'gloves': return 'gloves';
    case 'feet': case 'shoes': return 'shoes';
    case 'main_hand': case 'heldItem': return 'weapon_right';
    case 'off_hand': case 'shield': return 'weapon_left';
    case 'wings': return 'wings';
    default: return null;
  }
}

export function isEquipmentTemplateItem(item: Partial<Item> | null | undefined): item is Item {
  if (!item?.id || item.cat === 'lyra' || item.slot === 'body') return false;
  return ['arme', 'armure', 'aventurier', 'bouclier'].includes(item.cat || '') && !!unifiedEquipmentSlot(item);
}

const PROCEDURAL_MISSING_SLOT_TEMPLATE_IDS = new Set([
  'priest_sandals_f',
  'necklace_f',
  'fairy_wings_f',
]);

function equipmentFamily(item: Item, slot: EquipmentSlot): string {
  if (slot === 'weapon_left') return 'shield';
  if (slot === 'accessory') return 'accessory';
  if (slot === 'wings') return 'wings';
  const id = `${item.id} ${item.name || ''} ${item.class || ''}`.toLowerCase();
  if (slot !== 'weapon_right') {
    // Famille visuelle canonique des armures. Le héros 3D consomme cette
    // donnée ; il ne tente plus de deviner un costume depuis templateId.
    if (/(paladin|aegis|egide|solar|divine|aspirant)/.test(id)) return 'paladin';
    if (/(priest|pretre|prêtre|prophet|prophète|seraph|séraph|novice|sacre|sacré|bard|barde|virtuose|refrain)/.test(id)) return 'support';
    if (/(mage|archmage|arcane|cosmic|cosmique|robe|cloth|tissu|scholar|savant|apprenti)/.test(id)) return 'arcane';
    if (/(rogue|voleur|shadow|ombre|assassin|leather|cuir|tempest|tempête|ranger|pisteur)/.test(id)) return 'finesse';
    return 'martial';
  }
  if (/(baguette|baton|staff|grimoire|orb|sceptre|requiem)/.test(id)) return 'arcane';
  if (/(lute|harp)/.test(id)) return 'support';
  if (/(arc|bow|arbal)/.test(id)) return 'bow';
  if (/(dague|rapier)/.test(id)) return 'finesse';
  return 'martial';
}

export function equipmentTemplateFromItem(item: Item): EquipmentTemplate | null {
  const slot = unifiedEquipmentSlot(item);
  if (!slot || !isEquipmentTemplateItem(item)) return null;
  return {
    id: item.id,
    name: item.name,
    slot,
    family: equipmentFamily(item, slot),
    emoji: item.emoji,
    img: item.img,
    twoHanded: item.twoHanded,
    ambidextrous: item.ambidextrous,
    shieldOnly: item.shieldOnly,
    allowedAffixes: equipmentAffixPoolForFamily(equipmentFamily(item, slot), slot),
  };
}

/**
 * Modèles autorisés dans le pool procédural des donjons.
 * Les cosmétiques Aventurier, récompenses de titre et offres premium gardent
 * leur propre provenance et ne doivent jamais être tirés au hasard.
 */
export function proceduralEquipmentTemplateFromItem(item: Item): EquipmentTemplate | null {
  const ordinaryCombatModel = ['arme', 'armure', 'bouclier'].includes(item.cat);
  if ((!ordinaryCombatModel && !PROCEDURAL_MISSING_SLOT_TEMPLATE_IDS.has(item.id))
    || item.unlockTitle
    || item.hiddenFromShop
    || item.priceEssence != null) return null;
  return equipmentTemplateFromItem(item);
}

export function equipmentTemplatesFromCatalog(items: Record<string, Item>): EquipmentTemplate[] {
  return Object.values(items).map(proceduralEquipmentTemplateFromItem).filter((item): item is EquipmentTemplate => !!item);
}
